import React, { useEffect, useRef } from 'react';

import {
  computeGlowBlur,
  computeGlowHaloInsetPercent,
  computePresenceGlowVisuals,
} from '@/common/components/presence/presenceGlowBudget';
import { PresenceGlowStylesInjector } from '@/common/components/presence/PresenceGlowStylesInjector';
import type { AIPresenceState } from '@/common/components/presence/types';
import type { OrbPaletteStops } from '@/common/theme/orbPalette';

type PresenceGlowShellProps = {
  state: AIPresenceState;
  size: number;
  palette: OrbPaletteStops;
  punch: number;
  reducedMotion: boolean;
  testID?: string;
  children: React.ReactNode;
};

/**
 * Brand outer glow wrapping the nebula orb (web only).
 * Pulse scale is drawn in the canvas; this shell only blooms the halo (inset + blur).
 */
export function PresenceGlowShell({
  state,
  size,
  palette,
  punch,
  reducedMotion,
  testID = 'presence-glow-shell',
  children,
}: PresenceGlowShellProps) {
  const shellRef = useRef<HTMLDivElement>(null);

  const visuals = computePresenceGlowVisuals({
    state,
    punch,
    reducedMotion,
  });

  useEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    const blur = computeGlowBlur(size);
    node.style.setProperty('--presence-glow-scale', visuals.scale.toFixed(4));
    node.style.setProperty('--presence-glow-alpha', visuals.alpha.toFixed(4));
    node.style.setProperty('--presence-glow-color', palette.haloRgb.join(', '));
    node.style.setProperty('--presence-glow-blur', `${(blur * visuals.scale).toFixed(2)}px`);
    node.style.setProperty(
      '--presence-glow-halo-inset',
      `${computeGlowHaloInsetPercent(visuals.scale).toFixed(2)}%`,
    );
  }, [palette.haloRgb, size, visuals.alpha, visuals.scale]);

  return (
    <>
      <PresenceGlowStylesInjector />
      <div
        ref={shellRef}
        className="presence-glow-shell"
        style={{ width: size, height: size }}
        data-testid={testID}
        data-glow-alpha={visuals.alpha.toFixed(4)}
        data-glow-scale={visuals.scale.toFixed(4)}
        data-glow-reduced-motion={reducedMotion ? 'true' : 'false'}
        aria-hidden="true"
      >
        <div className="presence-glow-shell__halo" data-testid="presence-glow-halo" aria-hidden="true" />
        <div className="presence-glow-shell__content">
          {children}
        </div>
      </div>
    </>
  );
}
